// src/pages/SODashboard.js
import React, { useState, useEffect } from "react";
import { signOut } from "firebase/auth";
import { auth, db } from "../../firebase";
import { useNavigate } from "react-router-dom";
import {
  collection,
  query,
  where,
  getDocs,
  addDoc,
  serverTimestamp,
  doc,
  getDoc,
} from "firebase/firestore";
import styles from "../../styles/Dashboard/DirectSalesDashboard.module.css";
import { FiMenu } from "react-icons/fi";
import logo from "../../assets/logo.jpg";
import { ToastContainer, toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";

// const mergedCropList = [
//   "Bitter Gourd",
//   "Brinjal/Egg Plant",
//   "Cabbage",
//   "Cauliflower",
//   "Cotton",
//   "Coriander",
//   "Cucumber",
//   "Green Pea",
//   "Hybrid Bottle Gourd",
//   "Hybrid Corn",
//   "Hybrid Corn Fodder",
//   "Hybrid Mustard",
//   "Hybrid Rice",
//   "Melon",
//   "Mustard",
//   "Okra",
//   "Onion",
//   "Papaya",
//   "Pearl Millet",
//   "Paddy",
//   "Ridge Gourd",
//   "Sorghum Sudan Grass",
//   "Sponge Gourd",
//   "Squash",
//   "Sweet Pepper",
//   "Sultan Afgoi",
//   "Tomatoes",
//   "Turnip",
//   "Watermelon",
//   "Wheat",
//   "Hot Pepper",
//   "Muskmelon",
// ];

const DirectSalesDashboard = () => {
  const [activeTab, setActiveTab] = useState("placeOrder");
  const [parties, setParties] = useState([]);
  const [partyMobile, setPartyMobile] = useState("");
  const [pod, setPod] = useState([]);
  const [products, setProducts] = useState([]);
  const [selectedParty, setSelectedParty] = useState("");
  const [selectedProducts, setSelectedProducts] = useState([]);
  const [soName, setSoName] = useState("");
  const [contactInfo, setContactInfo] = useState("");
  const [orders, setOrders] = useState([]);
  const [loadingOrders, setLoadingOrders] = useState(false);
  const [filterParty, setFilterParty] = useState("");
  const [filterProduct, setFilterProduct] = useState("");
  const [filterStartDate, setFilterStartDate] = useState("");
  const [filterEndDate, setFilterEndDate] = useState("");
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [showRejectionModal, setShowRejectionModal] = useState(false);
  const [rejectionMessage, setRejectionMessage] = useState("");
  const [partySearchTerm, setPartySearchTerm] = useState("");
  const [filteredPartyOptions, setFilteredPartyOptions] = useState([]);
  const [showPartyDropdown, setShowPartyDropdown] = useState(false);
  const [commitmentOfPayment, setCommitmentOfPayment] = useState("");
  const [commitmentDate, setCommitmentDate] = useState("");
  /* Ledger feature disabled — commented out per request
  const [ledgerEntries, setLedgerEntries] = useState([]);
  const [totalLedgerBalance, setTotalLedgerBalance] = useState(0);
  */



  const [currentPage, setCurrentPage] = useState(1);
  const pageSize = 10;
  const navigate = useNavigate();

  const handleShowRejectionMessage = (message) => {
    setRejectionMessage(message || "No rejection message provided.");
    setShowRejectionModal(true);
  };

  /* Ledger feature disabled — commented out per request
  // Calculate total ledger balance whenever entries change
  useEffect(() => {
    const totalDebit = ledgerEntries.reduce((sum, entry) => sum + (parseFloat(entry.debit) || 0), 0);
    const totalCredit = ledgerEntries.reduce((sum, entry) => sum + (parseFloat(entry.credit) || 0), 0);
    setTotalLedgerBalance(totalCredit - totalDebit);
  }, [ledgerEntries]);
  */

  useEffect(() => {
    if (partySearchTerm.trim() === "") {
      setFilteredPartyOptions([]);
      setShowPartyDropdown(false);
    } else {
      const term = partySearchTerm.toLowerCase();
      const filtered = parties.filter(p =>
        p.name.toLowerCase().includes(term) ||
        (p.code || "").toLowerCase().includes(term)
      );
      // Exact party-code matches float to the top
      filtered.sort((a, b) => {
        const aExact = (a.code || "").toLowerCase() === term;
        const bExact = (b.code || "").toLowerCase() === term;
        if (aExact && !bExact) return -1;
        if (!aExact && bExact) return 1;
        return 0;
      });
      setFilteredPartyOptions(filtered);
      setShowPartyDropdown(true);
    }
  }, [partySearchTerm, parties]);

  useEffect(() => {
    const fetchData = async () => {
      if (!auth.currentUser) return;

      const partyRef = collection(db, "parties");
      const q = query(partyRef, where("assignedTo", "==", auth.currentUser.uid));
      const querySnapshot = await getDocs(q);
      setParties(querySnapshot.docs.map(doc => ({ id: doc.id, ...doc.data() })));

      const productsRef = collection(db, "products");
      const productsSnapshot = await getDocs(productsRef);
      setProducts(productsSnapshot.docs.map(doc => ({ id: doc.id, ...doc.data() })));

      const userDoc = await getDoc(doc(db, "users", auth.currentUser.uid));
      if (userDoc.exists()) {
        const userData = userDoc.data();
        setSoName(userData.name || "");
      }
    };
    fetchData();
  }, []);

  useEffect(() => {
    const fetchOrders = async () => {
      if (!auth.currentUser) return;
      setLoadingOrders(true);
      try {
        const ordersQuery = query(collection(db, "orders"), where("soId", "==", auth.currentUser.uid));
        const querySnapshot = await getDocs(ordersQuery);
        const ordersData = querySnapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
        const sorted = ordersData.sort((a, b) => {
          const dateA = a.createdAt?.toDate?.() || new Date(0);
          const dateB = b.createdAt?.toDate?.() || new Date(0);
          return dateB - dateA;
        });
        setOrders(sorted);
      } catch (err) {
        console.error("Error fetching orders:", err);
        setOrders([]);
      } finally {
        setLoadingOrders(false);
      }
    };

    if (activeTab === "orders") {
      fetchOrders();
    }
  }, [activeTab]);

  const handleLogout = async () => {
    await signOut(auth);
    navigate("/");
    window.location.reload();
  };

  const handleAddProduct = () => {
    setSelectedProducts([...selectedProducts, { productId: "", quantity: "" }]);
  };

  const handleProductChange = (index, field, value) => {
    const updated = [...selectedProducts];
    updated[index][field] = value;
    setSelectedProducts(updated);
  };

  const resetOrderForm = () => {
    setSelectedParty("");
    setPartySearchTerm("");
    setSelectedProducts([]);
    setPartyMobile("");
    setPod("");
    setContactInfo("");
    setCommitmentOfPayment("");
    setCommitmentDate("");
    /* Ledger feature disabled — commented out per request
    setLedgerEntries([]); // ✅ will clear to empty initialized state
    // setLedgerProductSearchTerm("");
    setTotalLedgerBalance(0);
    */
  };

  /* Ledger feature disabled — commented out per request
  // Ledger entries handlers
  const handleAddLedgerEntry = () => {
    setLedgerEntries([
      ...ledgerEntries,
      { productName: "", debit: "", credit: "", season: "" },
    ]);
  };

  const handleLedgerEntryChange = (index, field, value) => {
    const updated = [...ledgerEntries];
    updated[index][field] = value;

    // If changing product name, update the search term for filtering
    if (field === "productName") {
      // setLedgerProductSearchTerm(value);
    }

    setLedgerEntries(updated);
  };

  // Ledger product name selection in dropdown list
  // const handleLedgerProductSelect = (index, productName) => {
  //   const updated = [...ledgerEntries];
  //   updated[index].productName = productName;
  //   setLedgerEntries(updated);
  //   setLedgerProductSearchTerm("");
  //   setShowLedgerProductDropdown(false);
  // };

  // Remove ledger entry
  const handleRemoveLedgerEntry = (index) => {
    const updated = ledgerEntries.filter((_, i) => i !== index);
    setLedgerEntries(updated);
  };
  */

  const handleSubmitOrder = async () => {
    if (submitting) return;

    if (!selectedParty) {
      toast.error("Please Select a Valid Party.");
      return;
    }

    const selectedPartyObj = parties.find(p => p.name === selectedParty);
    if (!selectedPartyObj) {
      toast.error("Invalid Party Selected. Please Choose From The List.");
      return;
    }

    if (!partyMobile.trim()) {
      toast.error("Please Enter The Party Phone Number.");
      return;
    }

    if (!pod.trim()) {
      toast.error("Please Enter The POD.");
      return;
    }

    if (!contactInfo.trim()) {
      toast.error("Please Enter Contact Info.");
      return;
    }

    if (!commitmentOfPayment.trim()) {
      toast.error("Please enter Commitment Message");
      return;
    }
    if (!commitmentDate) {
      toast.error("Please select a Commitment Date");
      return;
    }


    if (selectedProducts.length === 0) {
      toast.error("Please Add At Least One Product.");
      return;
    }

    const incompleteProduct = selectedProducts.find(
      (p) => !p.productId || !p.quantity || parseInt(p.quantity) <= 0
    );

    if (incompleteProduct) {
      toast.error("Please Enter Valid Quantity");
      return;
    }

    /* Ledger feature disabled — commented out per request
    // ---------- Ledger validation ----------
    // Must have at least one ledger entry
    if (!Array.isArray(ledgerEntries) || ledgerEntries.length === 0) {
      toast.error("Please add the ledger of Party before submitting.");
      return;
    }

    // Each entry: season + productName required; exactly one of debit/credit > 0 and numeric
    for (const entry of ledgerEntries) {
      const season = (entry.season || "").trim();
      const productName = (entry.productName || "").trim();
      const debitNum = Number(entry.debit);
      const creditNum = Number(entry.credit);
      const hasDebit = !isNaN(debitNum) && debitNum > 0;
      const hasCredit = !isNaN(creditNum) && creditNum > 0;
      const debitEmpty = entry.debit === "" || entry.debit === null || typeof entry.debit === "undefined";
      const creditEmpty = entry.credit === "" || entry.credit === null || typeof entry.credit === "undefined";

      if (!season) {
        toast.error("Please select a season for all ledger entries.");
        return;
      }

      if (!productName) {
        toast.error("Please select product name for all ledger entries.");
        return;
      }

      // Must provide a numeric amount in either debit or credit
      if ((debitEmpty && creditEmpty) || (isNaN(debitNum) && isNaN(creditNum))) {
        toast.error(`Please enter a valid debit or credit amount for product "${productName}".`);
        return;
      }

      // Only one side allowed
      if (hasDebit && hasCredit) {
        toast.error(`Only one of Debit or Credit is allowed for product "${productName}".`);
        return;
      }

      // At least one side must be > 0
      if (!hasDebit && !hasCredit) {
        toast.error(`Either Debit or Credit must be greater than 0 for product "${productName}".`);
        return;
      }
    }
    */

    const partyCode = selectedPartyObj.code;
    const refCode = `ORD-${new Date().getFullYear()}-${Date.now().toString().slice(-6)}`;

    const enrichedProducts = selectedProducts.map(p => {
      const product = products.find(prod => prod.id === p.productId);
      return product
        ? {
          productId: p.productId,
          name: product.name || "",
          category: product.category || "N/A",
          variety: product.variety || "N/A",
          packSize: product.packSize || "N/A",
          packType: product.packType || "N/A",
          quantity: p.quantity,
        }
        : {
          productId: p.productId,
          name: "Unknown",
          category: "N/A",
          variety: "N/A",
          packSize: "N/A",
          packType: "N/A",
          quantity: p.quantity,
        };
    });

    /* Ledger feature disabled — commented out per request
    // Normalize ledger and compute totals
    const normalizedLedger = ledgerEntries.map(entry => {
      const d = Number(entry.debit);
      const c = Number(entry.credit);
      return {
        season: (entry.season || "").trim(),
        productName: (entry.productName || "").trim(),
        debit: !isNaN(d) ? d : 0,
        credit: !isNaN(c) ? c : 0,
      };
    });

    const totalDebit = normalizedLedger.reduce((sum, e) => sum + (e.debit || 0), 0);
    const totalCredit = normalizedLedger.reduce((sum, e) => sum + (e.credit || 0), 0);
    const ledgerBalance = totalCredit - totalDebit;
    */

    const order = {
      refCode,
      soId: auth.currentUser.uid,
      soName,
      createdBy: auth.currentUser.uid,
      createdByName: soName,
      partyCode,
      partyName: selectedParty,
      partyMobile,
      pod,
      contactInfo,
      commitmentOfPayment,
      commitmentDate,
      products: enrichedProducts,
      // ledger: normalizedLedger,
      // totalDebit,
      // totalCredit,
      // ledgerBalance,
      status: "Placed",
      createdAt: serverTimestamp(),
    };

    try {
      setSubmitting(true);
      await addDoc(collection(db, "orders"), order);
      toast.success("Order placed successfully!");
      resetOrderForm();
    } catch (err) {
      console.error("Failed to place order:", err);
      toast.error("Failed to place order. Please try again.");
    } finally {
      setSubmitting(false);
    }
  };

  const renderOrdersList = () => {
    if (loadingOrders) return <p>Loading orders...</p>;

    const filteredOrders = orders.filter(order => {
      const matchesParty = filterParty ? order.partyName === filterParty : true;
      const orderDate = order.createdAt?.toDate?.();
      const matchesStartDate = filterStartDate ? orderDate >= new Date(filterStartDate) : true;
      const matchesEndDate = filterEndDate ? orderDate <= new Date(filterEndDate + "T23:59:59") : true;
      const matchesProduct = filterProduct
        ? (order.products || []).some((p) => (p.name || "").trim() === filterProduct)
        : true;
      return matchesParty && matchesStartDate && matchesEndDate && matchesProduct;
    });

    const totalPages = Math.ceil(filteredOrders.length / pageSize);
    const paginatedOrders = filteredOrders.slice((currentPage - 1) * pageSize, currentPage * pageSize);

    if (!filteredOrders.length) return <p>No matching orders found.</p>;

    return (
      <>
        <div className={styles.responsiveTable}>
          <table className={styles.ordersTable}>
            <thead>
              <tr>
                <th>Date</th>
                <th>Party</th>
                <th>Party Number</th>
                <th>POD</th>
                <th>Contact Info</th>
                <th>Status</th>
                <th>Product</th>
                <th>Total Qty</th>
                {/* <th>Ledger Balance</th> */}
              </tr>
            </thead>
            <tbody>
              {paginatedOrders.map(order => {
                const filteredProducts = order.products.filter(p => p.addedBy !== "LM");
                const rowStyle =
                  order.status === "Approved"
                    ? { backgroundColor: "#d4edda" }
                    : order.status === "Rejected" || order.status === "Rejected By Logistic"
                      ? { backgroundColor: "#f8d7da" }
                      : {};

                return filteredProducts.length === 0 ? (
                  <tr key={order.id} style={rowStyle}>
                    <td>{order.createdAt?.toDate ? order.createdAt.toDate().toLocaleString() : "N/A"}</td>
                    <td>{order.partyName}</td>
                    <td>{order.partyMobile || "N/A"}</td>
                    <td>{order.pod || "N/A"}</td>
                    <td>{order.contactInfo || "N/A"}</td>
                    <td>{order.status}</td>
                    <td colSpan="2">No Products</td>
                  </tr>
                ) : (
                  filteredProducts.map((product, idx) => (
                      <tr key={`${order.id}-${idx}`} style={rowStyle}>
                        {idx === 0 && (
                          <>
                            <td rowSpan={filteredProducts.length}>
                              {order.createdAt?.toDate ? order.createdAt.toDate().toLocaleString() : "N/A"}
                            </td>
                            <td rowSpan={filteredProducts.length}>{order.partyName}</td>
                            <td rowSpan={filteredProducts.length}>{order.partyMobile || "N/A"}</td>
                            <td rowSpan={filteredProducts.length}>{order.pod || "N/A"}</td>
                            <td rowSpan={filteredProducts.length}>{order.contactInfo || "N/A"}</td>
                            <td rowSpan={filteredProducts.length}>
                              <span
                                className={`${styles.statusBadge} ${order.status.toLowerCase().includes("approved")
                                    ? styles.approved
                                    : order.status.toLowerCase().includes("rejected")
                                      ? styles.rejected
                                      : styles.pending
                                  }`}
                              >
                                {order.status}
                              </span>
                              {["Rejected", "Rejected By Logistic"].includes(order.status) &&
                                order.rejectionMessage && (
                                  <span
                                    onClick={() => handleShowRejectionMessage(order.rejectionMessage)}
                                    title="View Rejection Reason"
                                    style={{
                                      marginLeft: "8px",
                                      cursor: "pointer",
                                      color: "#dc3545",
                                      fontWeight: "bold",
                                    }}
                                  >
                                    ❗
                                  </span>
                                )}
                            </td>
                          </>
                        )}

                        <td>
                          {product.name} - {product.variety} - {product.packSize} {product.packType} × {product.quantity}
                        </td>

                        {/* ✅ Total Quantity should appear only once */}
                        {idx === 0 && (
                          <td rowSpan={filteredProducts.length}>
                            <strong>
                              {order.products?.reduce(
                                (sum, p) => sum + (parseFloat(p.quantity) || 0),
                                0
                              )}
                            </strong>
                          </td>
                        )}

                        {/* Ledger feature disabled — commented out per request
                        {idx === 0 && (
                          <td rowSpan={filteredProducts.length}>
                            {order.ledger && order.ledger.length > 0 ? (
                              <div className={styles.ledgerSummary}>
                                <div className={styles.netBalance}>
                                  <b>Balance: </b>
                                  {(() => {
                                    const balance = (order.ledger || []).reduce(
                                      (acc, entry) =>
                                        acc + (parseFloat(entry.credit) || 0) - (parseFloat(entry.debit) || 0),
                                      0
                                    );
                                    return balance >= 0 ? `${balance} Cr` : `${Math.abs(balance)} Dr`;
                                  })()}
                                </div>

                                <details>
                                  <summary className={styles.collapsibleButton}>View Ledger Entries</summary>
                                  {Object.entries(
                                    order.ledger.reduce((acc, entry) => {
                                      if (!acc[entry.productName]) acc[entry.productName] = [];
                                      acc[entry.productName].push(entry);
                                      return acc;
                                    }, {})
                                  ).map(([cropName, entries]) => (
                                    <div key={cropName} className={styles.cropGroup}>
                                      <div className={styles.cropName}>{cropName}</div>
                                      <table className={styles.cropLedgerTable}>
                                        <thead>
                                          <tr>
                                            <th>Season</th>
                                            <th>Debit</th>
                                            <th>Credit</th>
                                          </tr>
                                        </thead>
                                        <tbody>
                                          {entries.map((entry, i) => (
                                            <tr key={i}>
                                              <td>{entry.season || "N/A"}</td>
                                              <td>{entry.debit ? `Dr: ${parseFloat(entry.debit).toFixed(2)}` : ""}</td>
                                              <td>{entry.credit ? `Cr: ${parseFloat(entry.credit).toFixed(2)}` : ""}</td>
                                            </tr>
                                          ))}
                                        </tbody>
                                      </table>
                                    </div>
                                  ))}
                                </details>
                              </div>
                            ) : (
                              "N/A"
                            )}
                          </td>
                        )}
                        */}
                      </tr>
                    ))

                );
              })}
            </tbody>
          </table>
        </div>

        <div className={styles.pagination}>
          <button disabled={currentPage === 1} onClick={() => setCurrentPage(prev => prev - 1)}>
            Previous
          </button>
          <span style={{ margin: "0 10px" }}>
            Page {currentPage} of {totalPages}
          </span>
          <button disabled={currentPage === totalPages} onClick={() => setCurrentPage(prev => prev + 1)}>
            Next
          </button>
        </div>
      </>
    );
  };

  // helper functions for category constraints
  const isVegetableOrPearlMillat = (product) => {
    if (!product) return false;
    const name = product.name?.toLowerCase() || "";
    const category = product.category?.toLowerCase() || "";
    return category === "vegetables" || category === "pearl millet" || name.includes("pearl millet") || category === "hyrbid mustard" || category === "mustard" || name.includes("mustard");
  };

  const hasVegetableOrPearlMillatSelected = selectedProducts.some((p) => {
    const prod = products.find((prod) => prod.id === p.productId);
    return isVegetableOrPearlMillat(prod);
  });

  const hasOtherProductSelected = selectedProducts.some((p) => {
    const prod = products.find((prod) => prod.id === p.productId);
    return prod && !isVegetableOrPearlMillat(prod);
  });


  return (
    <div className={styles.dashboardContainer}>
      <ToastContainer position="top-center" />

      {/* 🔹 Mobile Header */}
      <div className={styles.mobileHeader}>
        <div className={styles.logoContainer}>
          <img src={logo || "/logo.jpg"} alt="Logo" className={styles.logo} />
          <h2 className={styles.dashboardTitle}>Dashboard</h2>
        </div>
        <p className={styles.userName}>{soName}</p>
        <button
          className={styles.hamburger}
          onClick={() => setSidebarOpen(!sidebarOpen)}
        >
          <FiMenu size={24} />
        </button>
      </div>

      {/* 🔹 Sidebar Overlay */}
      {sidebarOpen && (
        <div
          className={styles.overlay}
          onClick={() => setSidebarOpen(false)} // close on click outside
        ></div>
      )}

      <aside className={`${styles.sidebar} ${sidebarOpen ? styles.showSidebar : ""}`}>
        <div className={styles.sidebarHeader}>
          <div className={styles.logoContainer}>
            <img src={logo || "/logo.png"} alt="Logo" className={styles.logo} />
            <p className={styles.userName}>{soName}</p>
          </div>
        </div>

        <div className={styles.sidebarLinks}>
          <button
            onClick={() => setActiveTab("placeOrder")}
            className={activeTab === "placeOrder" ? styles.activeTab : ""}
          >
            Place Order
          </button>
          <button
            onClick={() => setActiveTab("orders")}
            className={activeTab === "orders" ? styles.activeTab : ""}
          >
            View Orders
          </button>
        </div>

        {/* 🔹 Footer Logout */}
        <div className={styles.sidebarFooter}>
          <button onClick={handleLogout} className={styles.logoutButton}>
            Logout
          </button>
        </div>
      </aside>

      {/* 🔹 Main Content */}
      <main className={styles.mainContent}>
        {activeTab === "placeOrder" && (
          <div className={styles.card}>
            <h3 className={styles.sectionTitle}>Place New Order</h3>

            {/* Party Selection */}
            <label>Party</label>
            <div className={styles.comboBoxWrapper}>
              <input
                type="text"
                className={styles.partySection}
                placeholder="Search and select a party"
                value={partySearchTerm || selectedParty}
                onChange={(e) => {
                  setPartySearchTerm(e.target.value);
                  setSelectedParty(""); // clear selection while typing
                }}
                onFocus={() => setShowPartyDropdown(true)}
                onBlur={() => setTimeout(() => setShowPartyDropdown(false), 150)}
              />

              {showPartyDropdown && filteredPartyOptions.length > 0 && (
                <ul className={styles.dropdownList}>
                  {filteredPartyOptions.map((p) => (
                    <li
                      key={p.id}
                      onClick={() => {
                        setSelectedParty(p.name);
                        setPartySearchTerm(p.name);
                        setShowPartyDropdown(false);
                      }}
                      className={
                        selectedParty === p.name ? styles.dropdownSelected : ""
                      }
                    >
                      {p.name}{p.code ? ` (${p.code})` : ""}
                    </li>
                  ))}
                </ul>
              )}
            </div>

            {/* Party Details */}
            <label>Party Phone Number</label>
            <input
              type="text"
              value={partyMobile}
              onChange={(e) => setPartyMobile(e.target.value)}
              className={styles.inputField}
              placeholder="Enter Party Phone Number"
            />

            <label>POD</label>
            <textarea
              value={pod}
              onChange={(e) => setPod(e.target.value)}
              placeholder="Enter Your POD"
              className={styles.textarea}
            />

            <label>Contact Info</label>
            <textarea
              value={contactInfo}
              onChange={(e) => setContactInfo(e.target.value)}
              placeholder="Enter Phone Number and Delivery Address"
              className={styles.textarea}
            />

            {/* Commitment Section */}
            <label>Commitment Message</label>
            <textarea
              value={commitmentOfPayment}
              onChange={(e) => setCommitmentOfPayment(e.target.value)}
              placeholder="Enter commitment details"
              className={styles.textarea}
            />

            <label>Commitment Date</label>
            <input
              type="date"
              value={commitmentDate}
              onChange={(e) => setCommitmentDate(e.target.value)}
              className={styles.inputField}
            />

            {/* Products */}
            {selectedProducts.map((product, index) => (
              <div key={index} className={styles.productRow}>
                <select
                  value={product.productId}
                  onChange={(e) => {
                    const selectedProd = products.find(
                      (p) => p.id === e.target.value
                    );
                    if (!selectedProd) return;

                    const isVegetableOrPearl = isVegetableOrPearlMillat(selectedProd);

                    if (isVegetableOrPearl && hasOtherProductSelected) {
                      toast.warning(
                        "You cannot select Vegetables or Pearl Millet with other products."
                      );
                      return;
                    }

                    if (!isVegetableOrPearl && hasVegetableOrPearlMillatSelected) {
                      toast.warning(
                        "You cannot select other products with Vegetables or Pearl Millet."
                      );
                      return;
                    }

                    handleProductChange(index, "productId", e.target.value);
                  }}
                  className={styles.productSelect}
                >
                  <option value="">Select Product</option>
                  {Array.from(new Set(products.map((p) => p.category))).map(
                    (category) => (
                      <optgroup label={category} key={category}>
                        {products
                          .filter((p) => p.category === category)
                          .map((p) => {
                            const disable =
                              (hasVegetableOrPearlMillatSelected &&
                                !isVegetableOrPearlMillat(p)) ||
                              (hasOtherProductSelected &&
                                isVegetableOrPearlMillat(p));

                            return (
                              <option
                                key={p.id}
                                value={p.id}
                                disabled={disable}
                              >
                                {p.name} - {p.variety} - {p.packSize} -{" "}
                                {p.packType}
                              </option>
                            );
                          })}
                      </optgroup>
                    )
                  )}
                </select>

                <input
                  type="number"
                  placeholder="Qty"
                  min="1"
                  value={product.quantity}
                  onChange={(e) =>
                    handleProductChange(index, "quantity", e.target.value)
                  }
                  className={styles.qtyInput}
                />
                <button
                  onClick={() => {
                    const updated = selectedProducts.filter((_, i) => i !== index);
                    setSelectedProducts(updated);
                  }}
                  className={styles.removeBtn}
                >
                  Remove
                </button>
              </div>
            ))}
            <button onClick={handleAddProduct} className={styles.addBtn}>
              + Add Product
            </button>

            {/* ✅ Total Quantity Summary */}
            {selectedProducts.length > 0 && (
              <div style={{ marginTop: "10px", fontWeight: "bold" }}>
                Total Quantity:{" "}
                {selectedProducts.reduce(
                  (sum, p) => sum + (parseFloat(p.quantity) || 0),
                  0
                )}
              </div>
            )}

            {/* Ledger feature disabled — commented out per request
            {"Ledger Form"}
            <div className={styles.ledgerForm}>
              <h4>Ledger Entries</h4>
              {ledgerEntries.map((entry, idx) => (
                <div key={idx} className={styles.ledgerRow}>
                  <div className={styles.inputGroup}>
                    <label>Season:</label>
                    <select
                      value={entry.season}
                      onChange={(e) =>
                        handleLedgerEntryChange(idx, "season", e.target.value)
                      }
                      className={styles.inputField}
                    >
                      <option value="">Select Season</option>
                      <option value="2020-21">2020-21</option>
                      <option value="2021-22">2021-22</option>
                      <option value="2022-23">2022-23</option>
                      <option value="2023-24">2023-24</option>
                      <option value="2024-25">2024-25</option>
                      <option value="2025-26">2025-26</option>
                    </select>
                  </div>
                  <div className={styles.inputGroup}>
                    <label>Crop:</label>
                    <select
                      value={entry.productName}
                      onChange={(e) =>
                        handleLedgerEntryChange(idx, "productName", e.target.value)
                      }
                      className={styles.inputField}
                    >
                      <option value="">Select Crop</option>
                      {mergedCropList.map((crop) => (
                        <option key={crop} value={crop}>
                          {crop}
                        </option>
                      ))}
                    </select>
                  </div>
                  <div className={styles.inputGroup}>
                    <label>Debit:</label>
                    <input
                      type="number"
                      min="0"
                      value={entry.debit}
                      onChange={(e) =>
                        handleLedgerEntryChange(idx, "debit", e.target.value)
                      }
                      placeholder="0.00"
                      className={styles.inputField}
                    />
                  </div>
                  <div className={styles.inputGroup}>
                    <label>Credit:</label>
                    <input
                      type="number"
                      min="0"
                      value={entry.credit}
                      onChange={(e) =>
                        handleLedgerEntryChange(idx, "credit", e.target.value)
                      }
                      placeholder="0.00"
                      className={styles.inputField}
                    />
                  </div>
                  <button
                    onClick={() => handleRemoveLedgerEntry(idx)}
                    className={styles.removeBtn}
                  >
                    Remove
                  </button>
                </div>
              ))}
              <button onClick={handleAddLedgerEntry} className={styles.addBtn}>
                + Add Entry
              </button>
            </div>

            {"Actions"}
            <div>Total Balance: {totalLedgerBalance}</div>
            */}
            <div className={styles.actionButtons}>
              <button
                onClick={handleSubmitOrder}
                className={styles.submitBtn}
                disabled={submitting}
              >
                {submitting ? "Submitting..." : "Submit Order"}
              </button>
              <button onClick={resetOrderForm} className={styles.cancelBtn}>
                Reset
              </button>
            </div>
          </div>
        )}

        {/* Orders Section */}
        {activeTab === "orders" && (
          <div className={styles.card}>
            <h2 className={styles.sectionTitle}>My Orders</h2>
            <div className={styles.orderCounts}>
              <span className={styles.totalBox}>
                Total Orders: {orders.length}
              </span>
              <span className={styles.pendingBox}>
                Pending: {orders.filter((o) => o.status === "Pending").length}
              </span>
              <span className={styles.approvedBox}>
                Approved: {orders.filter((o) => o.status === "Approved").length}
              </span>
              <span className={styles.rejectedBox}>
                Rejected:{" "}
                {
                  orders.filter(
                    (o) =>
                      o.status === "Rejected" ||
                      o.status === "Rejected By BM/RSM" ||
                      o.status === "Rejected By Logistic"
                  ).length
                }
              </span>
            </div>

            {/* Filters */}
            <div className={styles.filterContainer}>
              <label>Party: </label>
              <select
                value={filterParty}
                onChange={(e) => setFilterParty(e.target.value)}
              >
                <option value="">All</option>
                {parties.map((p) => (
                  <option key={p.id} value={p.name}>
                    {p.name}
                  </option>
                ))}
              </select>
              <label>Start Date: </label>
              <input
                type="date"
                value={filterStartDate}
                onChange={(e) => setFilterStartDate(e.target.value)}
              />
              <label>End Date: </label>
              <input
                type="date"
                value={filterEndDate}
                onChange={(e) => setFilterEndDate(e.target.value)}
              />
              <label>Product: </label>
              <select
                value={filterProduct}
                onChange={(e) => setFilterProduct(e.target.value)}
              >
                <option value="">All</option>
                {[...new Set(
                  orders.flatMap((o) =>
                    (o.products || [])
                      .map((p) => (p.name || "").trim())
                      .filter((name) => name && name.toLowerCase() !== "n/a")
                  )
                )].sort().map((product, idx) => (
                  <option key={idx} value={product}>{product}</option>
                ))}
              </select>
            </div>

            {/* Rejection Modal */}
            {showRejectionModal && (
              <div className={styles.modalOverlay}>
                <div className={styles.modalContent}>
                  <h3>Rejection Message</h3>
                  <p>{rejectionMessage}</p>
                  <button
                    onClick={() => setShowRejectionModal(false)}
                    className={styles.modalCloseBtn}
                  >
                    Close
                  </button>
                </div>
              </div>
            )}

            {renderOrdersList()}
          </div>
        )}
      </main>
    </div>

  );
};

export default DirectSalesDashboard;
